import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
interface SingleSelectDropdownProps {
  label: string;
  options: string[];
  selected: string;
  onChange: (selected: string) => void;
  placeholder?: string;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  description?: string;
}
export function SingleSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select an option...',
  otherValue,
  onOtherChange,
  description
}: SingleSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectOption = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };
  const hasOther = options.includes('Other');
  const showOtherInput = hasOther && selected === 'Other';
  return <div data-id="element-1381">
      <label className="block text-[#1A3D3A] mb-3" style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '16px',
      fontWeight: 500
    }} data-id="element-1382">
        {label}
      </label>
      {description && <p className="text-[#3D5654] text-sm mb-3" style={{
      fontFamily: "'DM Sans', sans-serif"
    }} data-id="element-1383">
          {description}
        </p>}

      <div ref={dropdownRef} className="relative" data-id="element-1384">
        {/* Selected item display */}
        <div onClick={() => setIsOpen(!isOpen)} className="min-h-[48px] px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg cursor-pointer hover:border-[#1A3D3A]/40 transition-colors flex items-center justify-between gap-2" data-id="element-1385">
          <span className={selected ? 'text-[#1A3D3A]' : 'text-[#3D5654]/50'} style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '15px'
        }} data-id="element-1386">
            {selected || placeholder}
          </span>
          <ChevronDown className={`w-5 h-5 text-[#3D5654] transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1387" />
        </div>

        {/* Dropdown menu */}
        <AnimatePresence data-id="element-1388">
          {isOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.15
        }} className="absolute z-10 w-full mt-2 bg-white border-2 border-[#1A3D3A]/20 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-id="element-1389">
              {options.map(option => <button key={option} onClick={() => selectOption(option)} className="w-full px-4 py-3 text-left hover:bg-[#F8FAF9] transition-colors flex items-center justify-between gap-3" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '15px'
          }} data-id="element-1390">
                  <span className="text-[#1A3D3A]" data-id="element-1391">{option}</span>
                  {selected === option && <Check className="w-5 h-5 text-[#1A3D3A] flex-shrink-0" data-id="element-1392" />}
                </button>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Other input */}
      {showOtherInput && onOtherChange && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="mt-3" data-id="element-1393">
          <input type="text" value={otherValue || ''} onChange={e => onOtherChange(e.target.value)} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
        fontFamily: "'DM Sans', sans-serif"
      }} placeholder="Please specify..." data-id="element-1394" />
        </motion.div>}
    </div>;
}